import type { MfaClient, MfaPendingInfo } from '@platform/api-client';
import { useI18n } from '@platform/i18n';
import { useState, type ReactNode } from 'react';
import { Button } from '../components/button';
import { Checkbox } from '../components/checkbox';
import { Input } from '../components/input';
import { Label } from '../components/label';
import { Alert } from '../components/misc';
import { OtpInput } from '../components/otp-input';
import { useErrorText } from './errors';

type Method = NonNullable<MfaPendingInfo['methods']>[number];
export interface MfaChallengeProps {
  pending: MfaPendingInfo;
  client: MfaClient;
  authenticatePasskey: (options: unknown) => Promise<unknown>;
  onDone: () => void;
  onCancel: () => void;
  enrollment?: ReactNode;
}

/** Shared challenge flow: enrolled passkey first, with explicit alternative methods. */
export function MfaChallengeView({ pending, client, authenticatePasskey, onDone, onCancel, enrollment }: MfaChallengeProps) {
  const { t } = useI18n(); const errors = useErrorText(); const methods = pending.methods ?? [];
  const [method, setMethod] = useState<Method | undefined>(() => (['passkey', 'totp', 'recovery', 'temporary'] as const).find(value => methods.includes(value)));
  const [code, setCode] = useState(''); const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const [enroll, setEnroll] = useState(pending.enrollmentRequired && methods.length === 0);
  const [alternatives, setAlternatives] = useState(false);
  const verify = async () => {
    if (!method) return;
    setBusy(true); setError('');
    try {
      const assertion = method === 'passkey' ? await authenticatePasskey(await client.options()) : undefined;
      const result = await client.verify({ method, ...(method === 'passkey' ? { assertion } : { code }), remember });
      if (result.enrollmentRequired) setEnroll(true); else onDone();
    } catch (error: unknown) { setError(errors.describe(error)); }
    finally { setBusy(false); setCode(''); }
  };
  const label = (value: Method) => t(value === 'passkey' ? 'mfa.usePasskey' : value === 'totp' ? 'mfa.totp' : value === 'recovery' ? 'mfa.recovery' : 'mfa.temporary');
  const otherMethods = methods.filter(value => value !== method);
  return <div className="flex flex-col gap-3">
    {enroll ? enrollment ?? <Alert>{t('mfa.enrollmentRequired')}</Alert> : <>
      <p className="text-sm text-muted-foreground">{t('mfa.challenge')}</p>
      <form className="flex flex-col gap-3" onSubmit={event => { event.preventDefault(); void verify(); }}>
        {method === 'totp' ? <OtpInput label={t('mfa.code')} digitLabel={position => t('mfa.digit', { position })} value={code} onValueChange={setCode} disabled={busy} autoFocus /> : method && method !== 'passkey' ? <div className="flex flex-col gap-2">
          <Label htmlFor="mfa-access-code">{label(method)}</Label>
          <Input id="mfa-access-code" value={code} autoComplete="one-time-code" onChange={event => setCode(event.target.value)} disabled={busy} autoFocus />
        </div> : null}
        {(pending.rememberDays ?? 0) > 0 && <Label className="flex items-center gap-2"><Checkbox checked={remember} disabled={busy} onCheckedChange={value => setRemember(value === true)} />{t('mfa.remember')}</Label>}
        {error && <Alert variant="destructive">{error}</Alert>}
        {method && <Button type="submit" disabled={busy || (method === 'totp' ? !/^\d{6}$/.test(code) : method !== 'passkey' && !code.trim())}>{method === 'passkey' ? t('mfa.usePasskey') : t('mfa.verify')}</Button>}
      </form>
      {method === 'passkey' && methods.includes('totp') && <Button variant="outline" disabled={busy} onClick={() => { setMethod('totp'); setCode(''); setError(''); }}>{t('mfa.useAuthenticator')}</Button>}
      {otherMethods.some(value => method !== 'passkey' || value !== 'totp') && <>
        <Button variant="ghost" disabled={busy} aria-expanded={alternatives} onClick={() => setAlternatives(value => !value)}>{t('mfa.otherMethod')}</Button>
        {alternatives && <div className="flex flex-col gap-2">{otherMethods.filter(value => method !== 'passkey' || value !== 'totp').map(value => <Button key={value} variant="outline" disabled={busy} onClick={() => { setMethod(value); setCode(''); setError(''); setAlternatives(false); }}>{label(value)}</Button>)}</div>}
      </>}
    </>}
    <Button variant="ghost" disabled={busy} onClick={() => { setBusy(true); void client.cancel().then(onCancel, onCancel); }}>{t('common.cancel')}</Button>
  </div>;
}
